import React, { useState, useEffect } from "react";
import api from "api";
import styles from "../stripe-dashboard.module.css";
import AppModal from "components/ui/AppModal/AppModal";

const EMPTY = {
  name:               "",
  percent_off:        "",
  amount_off:         "",
  duration:           "once",
  duration_in_months: "",
};

const DURATION_LABELS = {
  once:      "Uma vez",
  forever:   "Para sempre",
  repeating: "Repetir por meses",
};

const ManageCouponsTab = ({ initialCoupons, token, onCouponChange }) => {
  const [coupons, setCoupons] = useState(initialCoupons);
  const [loading, setLoading] = useState(false);
  const [coupon, setCoupon]   = useState(EMPTY);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [feedbackModal, setFeedbackModal] = useState({
    open: false,
    variant: "custom",
    title: "",
    message: "",
    confirmText: "OK"
  });

  const closeFeedbackModal = () =>
    setFeedbackModal(prev => ({ ...prev, open: false }));

  useEffect(() => setCoupons(initialCoupons), [initialCoupons]);

  const handle = e => {
    const { name, value } = e.target;
    setCoupon(p => ({ ...p, [name]: value }));
  };

  const handleCreate = async e => {
    e.preventDefault();
    setLoading(true);
    const payload = { ...coupon };
    if (payload.amount_off) payload.amount_off = Math.round(payload.amount_off * 100);
    if (!payload.percent_off) delete payload.percent_off;
    if (!payload.amount_off)  delete payload.amount_off;
    try {
      await api.post("/v2/stripe/coupons", payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFeedbackModal({
        open: true,
        variant: "success",
        title: "Sucesso!",
        message: "Cupom criado.",
      });
      setCoupon(EMPTY);
      onCouponChange();
    } catch (err) {
      setFeedbackModal({
        open: true,
        variant: "error",
        title: "Erro",
        message: err.response?.data?.detail || "Verifique os campos.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async id => {
    if (deleteLoading) return;
    setDeleteConfirm({ open: true, id });
  };

  const confirmDelete = async () => {
    const id = deleteConfirm.id;
    if (!id || deleteLoading) return;

    setDeleteConfirm({ open: false, id: null });
    setDeleteLoading(true);

    try {
      await api.delete(`/v2/stripe/coupons/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFeedbackModal({
        open: true,
        variant: "success",
        title: "Excluído!",
        message: "Cupom removido.",
      });
      setCoupons(prev => prev.filter(c => c.id !== id));
    } catch (err) {
      setFeedbackModal({
        open: true,
        variant: "error",
        title: "Erro",
        message: err.response?.data?.detail || "Tente novamente.",
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <>
      {/* Criar cupom */}
      <div className={styles.card} style={{ marginBottom: "1.25rem" }}>
        <p className={styles.cardTitle}>Criar Novo Cupom</p>
        <form onSubmit={handleCreate} noValidate>
          <div className={styles.formBody}>

            <div className={styles.formGrid2}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Nome (Opcional)</label>
                <input
                  type="text"
                  name="name"
                  className={styles.formControl}
                  value={coupon.name}
                  onChange={handle}
                  placeholder="Ex: PROMO20"
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Duração</label>
                <select
                  name="duration"
                  className={styles.formControl}
                  value={coupon.duration}
                  onChange={handle}
                >
                  <option value="once">Apenas uma vez</option>
                  <option value="forever">Para sempre</option>
                  <option value="repeating">Repetir por meses</option>
                </select>
              </div>
            </div>

            <div className={styles.formGrid2}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Desconto Fixo (R$)</label>
                <input
                  type="number"
                  name="amount_off"
                  className={styles.formControl}
                  value={coupon.amount_off}
                  onChange={handle}
                  placeholder="Ex: 10.50"
                  disabled={!!coupon.percent_off}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Desconto Percentual (%)</label>
                <input
                  type="number"
                  name="percent_off"
                  className={styles.formControl}
                  value={coupon.percent_off}
                  onChange={handle}
                  placeholder="Ex: 20"
                  disabled={!!coupon.amount_off}
                />
              </div>
            </div>

            {coupon.duration === "repeating" && (
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Duração em Meses</label>
                <input
                  type="number"
                  name="duration_in_months"
                  className={styles.formControl}
                  value={coupon.duration_in_months}
                  onChange={handle}
                  placeholder="Ex: 3"
                />
              </div>
            )}

          </div>

          <div className={styles.formActions}>
            <button
              type="submit"
              className={`${styles.button} ${styles.buttonPrimary}`}
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className={styles.spinner} style={{ width: 14, height: 14, borderWidth: 2 }} />
                  Criando...
                </>
              ) : (
                <>
                  <i className="fas fa-ticket-alt" /> Criar Cupom
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Cupons existentes */}
      <div className={styles.card}>
        <p className={styles.cardTitle}>Cupons Existentes</p>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>ID</th>
                <th>Nome</th>
                <th>Desconto</th>
                <th>Duração</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {coupons.map(c => (
                <tr key={c.id}>
                  <td><span className={styles.mono}>{c.id}</span></td>
                  <td>{c.name || <span style={{ color: "#94a3b8" }}>—</span>}</td>
                  <td>
                    <span className={styles.mono} style={{ color: "#16a34a", fontWeight: 600 }}>
                      {c.percent_off
                        ? `${c.percent_off}%`
                        : `R$ ${(c.amount_off / 100).toFixed(2)}`}
                    </span>
                  </td>
                  <td>
                    <span style={{ fontSize: "0.8rem", color: "#64748b" }}>
                      {DURATION_LABELS[c.duration] || c.duration}
                    </span>
                  </td>
                  <td>
                    <button
                      type="button"
                      className={`${styles.button} ${styles.buttonDanger}`}
                      onClick={() => handleDelete(c.id)}
                    >
                      <i className="fas fa-trash" />
                    </button>
                  </td>
                </tr>
              ))}
              {coupons.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    style={{ textAlign: "center", color: "#94a3b8", padding: "2rem", fontStyle: "italic" }}
                  >
                    Nenhum cupom encontrado
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <AppModal
        isOpen={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, id: null })}
        allowOutsideClick={false}
        variant="warning"
        title="Excluir cupom?"
        message="Esta ação não pode ser desfeita."
        confirmText="Excluir"
        cancelText="Cancelar"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm({ open: false, id: null })}
      />

      <AppModal
        isOpen={deleteLoading}
        onClose={() => {}}
        allowOutsideClick={false}
        variant="loading"
        title="Excluindo..."
        message="Aguarde enquanto o cupom é removido."
      />

      <AppModal
        isOpen={feedbackModal.open}
        onClose={closeFeedbackModal}
        allowOutsideClick={true}
        variant={feedbackModal.variant}
        title={feedbackModal.title}
        message={feedbackModal.message}
        confirmText={feedbackModal.confirmText}
        cancelText={null}
        onConfirm={closeFeedbackModal}
        onCancel={closeFeedbackModal}
      />
    </>
  );
};

export { ManageCouponsTab };
